import { useState, useCallback } from 'react';
import {
  mapWatchToEventType,
  calculateSeverity,
  generateEventTitle,
  generateEventSummary,
} from '../../../services/eventBus/eventHelpers';
import {
  FIELD_ALIAS_MAP,
  REVERSE_ALIAS_MAP,
  getBrandConfigPath,
  getVariableId,
  resolveNestedPath,
  setNestedPath,
  readVariableValue,
  writeVariableValue,
} from '../../../data/businessArchitect/fieldAliasMap';
import { SEED_FORMULARY_PRODUCTS } from '../../../data/mf/seedFormularyProducts';
import { SEED_INGREDIENT_REGISTRY } from '../../../data/mf/seedIngredientRegistry';
import { SEED_INTERACTIONS } from '../../../data/mf/seedInteractions';
import {
  parsePriceToCents,
  generateStripeConfig,
  generateChartOfAccounts,
  generateSyncRules,
  generatePharmacyReconRules,
  generateInterEntityTransactions,
} from '../../../services/financial/configEngine';
import {
  scanContent,
  calculateDebtScore,
  calculateHotspots,
  suggestFix,
  SEVERITY_WEIGHT,
} from '../../../utils/debtScannerCore';
import type { DocBrand } from '../../../types/docops';

// ── Design tokens (matches Platform Health) ──

const C = {
  text: '#18181B',
  textSec: '#52525B',
  textMuted: '#A1A1AA',
  rose: '#E11D48',
  roseSoft: '#FFF1F2',
  pass: '#059669',
  passBg: '#D1FAE5',
  warn: '#B45309',
  warnBg: '#FEF3C7',
  fail: '#E11D48',
  failBg: '#FFF1F2',
  bgCard: '#FFFFFF',
  border: '#E4E4E7',
};

// ── Test Framework ──

interface TestResult {
  id: string;
  name: string;
  module: string;
  status: 'pass' | 'fail' | 'running' | 'pending';
  message?: string;
  durationMs?: number;
}

interface TestSuite {
  module: string;
  icon: string;
  tests: { id: string; name: string; fn: () => boolean | string }[];
}

// ── Financial Fixture ──

function makeTestBrand(overrides?: Partial<DocBrand['config']>): DocBrand {
  const base: DocBrand = {
    id: 'test-brand-001',
    name: 'TestBrand Health',
    status: 'active',
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-01T00:00:00Z',
    config: {
      brandName: 'TestBrand Health',
      brandDomain: 'testbrand.com',
      brandEntity: 'TestBrand LLC',
      stateOfFormation: 'FL',
      ein: '12-3456789',
      marketVertical: 'hormone_optimization',
      productType: 'health_platform',
      effectiveDate: '2025-01-01',
      ceoName: 'Jane Doe',
      ceoEmail: 'jane@testbrand.com',
      medicalDirectorName: 'Dr. Smith',
      mdCredentials: 'MD, FACEP',
      mdLicenseStates: ['FL', 'NY'],
      pcEntityName: 'TestBrand Medical PC',
      pcOwnerName: 'Dr. Smith',
      trustName: 'TestBrand IP Trust',
      trustTrusteeName: 'Jane Doe',
      telehealthNetwork: 'WellSync',
      pharmacyPartner: 'Empower Pharmacy',
      paymentProcessor: 'Stripe',
      fulfillmentPartner: 'ShipBob',
      cloudProvider: 'Google Cloud',
      consultationFee: 49,
      telehealthCost: 25,
      pcRetainer: 2500,
      mdRetainer: 3000,
      ipLicenseRoyaltyPercent: 5,
      supplementMarginPercent: '40%',
      legalEmail: 'legal@testbrand.com',
      privacyEmail: 'privacy@testbrand.com',
      supportEmail: 'support@testbrand.com',
      complianceEmail: 'compliance@testbrand.com',
      businessAddress: '123 Main St, Miami FL 33101',
      registeredAgentAddress: '123 Main St, Miami FL 33101',
      jurisdictions: ['FL', 'NY'],
      arbitrationCounty: 'Miami-Dade',
      tier1Price: '$49/mo',
      tier1PriceDescription: '$49 per month',
      dmcaEmail: 'dmca@testbrand.com',
      lastUpdatedDate: '2025-01-01',
      privacyPolicyUrl: '',
      chdPolicyUrl: '',
      hipaaNoticeUrl: '',
      telehealthConsentUrl: '',
      cookiePolicyUrl: '',
      tiers: {
        tier1: { name: 'Essentials', enabled: true, pricingModel: 'paid', price: '$49/mo', priceDescription: '$49/mo', refundWindow: '30 days', refundPolicy: 'Full refund' },
        tier2: { name: 'Premium', enabled: true, pricingModel: 'paid', price: '$149/mo', priceDescription: '$149/mo', refundWindow: '30 days', refundPolicy: 'Full refund' },
        tier3: { name: 'Concierge', enabled: false, pricingModel: 'paid', price: '$299/mo', priceDescription: '$299/mo', refundWindow: '14 days', refundPolicy: 'Prorated' },
      },
    },
  };
  if (overrides) {
    base.config = { ...base.config, ...overrides };
  }
  return base;
}

// ════════════════════════════════════════════
// Test Suites (5 modules)
// ════════════════════════════════════════════

function buildAllSuites(): TestSuite[] {
  return [
    buildEventBusTests(),
    buildBusinessArchitectTests(),
    buildMasterFormulatorTests(),
    buildFinancialTests(),
    buildCodebaseIntelligenceTests(),
  ];
}

// ── 1. Event Bus (EB-1 → EB-9) ──

function buildEventBusTests(): TestSuite {
  return {
    module: 'Event Bus',
    icon: 'EB',
    tests: [
      { id: 'EB-1', name: 'mapWatchToEventType — FDA rule maps correctly',
        fn: () => mapWatchToEventType('fda_rule') === 'FDA_RULE_CHANGE' || 'Expected FDA_RULE_CHANGE' },
      { id: 'EB-2', name: 'mapWatchToEventType — case insensitive',
        fn: () => mapWatchToEventType('FDA_RULE') === 'FDA_RULE_CHANGE' || 'Expected FDA_RULE_CHANGE for uppercase' },
      { id: 'EB-3', name: 'mapWatchToEventType — unknown returns null',
        fn: () => mapWatchToEventType('unknown_type') === null || 'Expected null' },
      { id: 'EB-4', name: 'mapWatchToEventType — all event types reachable',
        fn: () => {
          const types = new Set<string>();
          const inputs = [
            'fda_rule','fda_enforcement','fda_ingredient','state_law','dea_telehealth',
            'cms_reimbursement','hhs_policy','posture_reversal','interaction','study_retracted',
            'formulary_drift','md_expiry','biomarker_range','guideline_update','xprize',
            'compliance_deadline','doc_regen','breach_risk','system_health','debt_threshold',
          ];
          for (const i of inputs) { const r = mapWatchToEventType(i); if (r) types.add(r); }
          return types.size >= 19 || `Only ${types.size}/20 event types reachable`;
        },
      },
      { id: 'EB-5', name: 'calculateSeverity — POSTURE_REVERSAL is critical',
        fn: () => calculateSeverity('POSTURE_REVERSAL', {}) === 'critical' || 'Expected critical' },
      { id: 'EB-6', name: 'calculateSeverity — deadline escalation works',
        fn: () => {
          const base = calculateSeverity('XPRIZE_UPDATE', {});
          const esc = calculateSeverity('XPRIZE_UPDATE', { deadlineDays: 3 });
          return base === 'low' && esc === 'medium' || `Expected low→medium, got ${base}→${esc}`;
        },
      },
      { id: 'EB-7', name: 'generateEventTitle — uses payload title',
        fn: () => generateEventTitle('FDA_RULE_CHANGE', { title: 'Custom Title' }) === 'Custom Title' || 'Expected Custom Title' },
      { id: 'EB-8', name: 'generateEventTitle — builds from type + subject',
        fn: () => {
          const t = generateEventTitle('STATE_LAW_CHANGE', { subject: 'California' });
          return t.includes('State Law Change') && t.includes('California') || `Got '${t}'`;
        },
      },
      { id: 'EB-9', name: 'generateEventSummary — contextual summary',
        fn: () => {
          const s = generateEventSummary('INTERACTION_DISCOVERED', { compounds: 'DHEA + Warfarin' });
          return s.includes('DHEA + Warfarin') || `Got '${s}'`;
        },
      },
    ],
  };
}

// ── 2. Business Architect (BA-1 → BA-10) ──

function buildBusinessArchitectTests(): TestSuite {
  return {
    module: 'Business Architect',
    icon: 'BA',
    tests: [
      { id: 'BA-1', name: 'Forward alias — legalEntityName → brandEntity',
        fn: () => getBrandConfigPath('legalEntityName') === 'brandEntity' || `Got '${getBrandConfigPath('legalEntityName')}'` },
      { id: 'BA-2', name: 'Reverse alias — brandEntity → legalEntityName',
        fn: () => getVariableId('brandEntity') === 'legalEntityName' || `Got '${getVariableId('brandEntity')}'` },
      { id: 'BA-3', name: 'Unknown field passes through',
        fn: () => getBrandConfigPath('unknownField') === 'unknownField' || 'Expected passthrough' },
      { id: 'BA-4', name: 'Alias map has 60+ entries',
        fn: () => { const c = Object.keys(FIELD_ALIAS_MAP).length; return c >= 60 || `Only ${c} entries`; } },
      { id: 'BA-5', name: 'Reverse map matches forward map size',
        fn: () => { const f = Object.keys(FIELD_ALIAS_MAP).length; const r = Object.keys(REVERSE_ALIAS_MAP).length; return f === r || `Fwd: ${f}, Rev: ${r}`; } },
      { id: 'BA-6', name: 'resolveNestedPath — reads tiers.tier1.name',
        fn: () => resolveNestedPath({ tiers: { tier1: { name: 'Basic' } } }, 'tiers.tier1.name') === 'Basic' || 'Expected Basic' },
      { id: 'BA-7', name: 'setNestedPath — creates intermediates',
        fn: () => { const o: Record<string, any> = {}; setNestedPath(o, 'tiers.tier2.name', 'Premium'); return o.tiers?.tier2?.name === 'Premium' || 'Expected Premium'; } },
      { id: 'BA-8', name: 'readVariableValue — flat field',
        fn: () => readVariableValue('legalEntityName', { brandEntity: 'TestCo LLC' }) === 'TestCo LLC' || 'Expected TestCo LLC' },
      { id: 'BA-9', name: 'readVariableValue — nested tier field',
        fn: () => readVariableValue('tier1Name', { tiers: { tier1: { name: 'Starter' } } }) === 'Starter' || 'Expected Starter' },
      { id: 'BA-10', name: 'writeVariableValue — writes through alias',
        fn: () => { const c: Record<string, any> = {}; writeVariableValue('legalEntityName', 'NewCo LLC', c); return c.brandEntity === 'NewCo LLC' || `Got '${c.brandEntity}'`; } },
    ],
  };
}

// ── 3. Master Formulator (MF-1 → MF-10) ──

function buildMasterFormulatorTests(): TestSuite {
  return {
    module: 'Master Formulator',
    icon: 'MF',
    tests: [
      { id: 'MF-1', name: 'Seed products — 10 products loaded',
        fn: () => SEED_FORMULARY_PRODUCTS.length === 10 || `Got ${SEED_FORMULARY_PRODUCTS.length}` },
      { id: 'MF-2', name: 'Every product has required fields',
        fn: () => { for (const p of SEED_FORMULARY_PRODUCTS) { if (!p.productId) return `Missing productId`; if (!p.productName) return `${p.productId} missing productName`; if (!p.ingredients?.length) return `${p.productId} has no ingredients`; } return true; } },
      { id: 'MF-3', name: 'Seed ingredients — 10+ ingredients loaded',
        fn: () => SEED_INGREDIENT_REGISTRY.length >= 10 || `Got ${SEED_INGREDIENT_REGISTRY.length}` },
      { id: 'MF-4', name: 'Every ingredient has required fields',
        fn: () => { for (const i of SEED_INGREDIENT_REGISTRY) { if (!i.ingredientId) return `Missing ingredientId`; if (!i.commonName) return `${i.ingredientId} missing commonName`; if (!i.fdaStatus) return `${i.ingredientId} missing fdaStatus`; } return true; } },
      { id: 'MF-5', name: 'Seed interactions — 15+ interactions loaded',
        fn: () => SEED_INTERACTIONS.length >= 15 || `Got ${SEED_INTERACTIONS.length}` },
      { id: 'MF-6', name: 'Critical interactions require block action',
        fn: () => { const c = SEED_INTERACTIONS.filter((i: any) => i.severity === 'critical'); for (const i of c) { if (i.actionRequired !== 'block') return `${i.interactionId} is critical but action='${i.actionRequired}'`; } return c.length > 0 || 'No critical interactions found'; } },
      { id: 'MF-7', name: 'Product ingredients reference valid registry entries',
        fn: () => { const ids = new Set(SEED_INGREDIENT_REGISTRY.map((i: any) => i.ingredientId)); for (const p of SEED_FORMULARY_PRODUCTS) { for (const ing of (p.ingredients || [])) { if (!ids.has(ing.ingredientId)) return `${p.productId} references unknown ${ing.ingredientId}`; } } return true; } },
      { id: 'MF-8', name: 'All products are supplements (seed set)',
        fn: () => { for (const p of SEED_FORMULARY_PRODUCTS) { if (p.productType !== 'supplement') return `${p.productId} is '${p.productType}'`; } return true; } },
      { id: 'MF-9', name: 'Every interaction has mechanism + clinicalEffect',
        fn: () => { for (const i of SEED_INTERACTIONS) { if (!i.mechanism) return `${i.interactionId} missing mechanism`; if (!i.clinicalEffect) return `${i.interactionId} missing clinicalEffect`; } return true; } },
      { id: 'MF-10', name: 'Product verticals span multiple categories',
        fn: () => { const v = new Set(SEED_FORMULARY_PRODUCTS.map((p: any) => p.vertical)); return v.size >= 3 || `Only ${v.size} verticals`; } },
    ],
  };
}

// ── 4. Financial (FI-1 → FI-32) ──

function buildFinancialTests(): TestSuite {
  return {
    module: 'Financial',
    icon: 'FI',
    tests: [
      { id: 'FI-1', name: 'parsePriceToCents — "$49/mo" → 4900',
        fn: () => parsePriceToCents('$49/mo') === 4900 || `Got ${parsePriceToCents('$49/mo')}` },
      { id: 'FI-2', name: 'parsePriceToCents — "$149.99" → 14999',
        fn: () => parsePriceToCents('$149.99') === 14999 || `Got ${parsePriceToCents('$149.99')}` },
      { id: 'FI-3', name: 'parsePriceToCents — "$299/month" → 29900',
        fn: () => parsePriceToCents('$299/month') === 29900 || `Got ${parsePriceToCents('$299/month')}` },
      { id: 'FI-4', name: 'parsePriceToCents — empty string → 0',
        fn: () => parsePriceToCents('') === 0 || `Got ${parsePriceToCents('')}` },
      { id: 'FI-5', name: 'parsePriceToCents — "not a price" → 0',
        fn: () => parsePriceToCents('not a price') === 0 || `Got ${parsePriceToCents('not a price')}` },
      { id: 'FI-6', name: 'generateStripeConfig — products for enabled paid tiers',
        fn: () => { const c = generateStripeConfig(makeTestBrand()); const s = c.products.filter(p => p.metadata.revenueStream === 'subscription'); return s.length === 2 || `Got ${s.length}`; } },
      { id: 'FI-7', name: 'generateStripeConfig — skips disabled tiers',
        fn: () => { const c = generateStripeConfig(makeTestBrand()); const t3 = c.products.filter(p => p.metadata.tier === 'tier3'); return t3.length === 0 || `Got ${t3.length} tier3 products`; } },
      { id: 'FI-8', name: 'generateStripeConfig — includes consultation product',
        fn: () => { const c = generateStripeConfig(makeTestBrand()); const co = c.products.filter(p => p.metadata.revenueStream === 'consultation'); return co.length === 1 || `Got ${co.length}`; } },
      { id: 'FI-9', name: 'generateStripeConfig — no consultation without telehealth',
        fn: () => { const c = generateStripeConfig(makeTestBrand({ telehealthNetwork: '', consultationFee: 49 })); const co = c.products.filter(p => p.metadata.revenueStream === 'consultation'); return co.length === 0 || `Got ${co.length}`; } },
      { id: 'FI-10', name: 'generateStripeConfig — correct price in cents',
        fn: () => { const c = generateStripeConfig(makeTestBrand()); const t1 = c.products.find(p => p.metadata.tier === 'tier1'); return t1?.priceInCents === 4900 || `Got ${t1?.priceInCents}`; } },
      { id: 'FI-11', name: 'generateStripeConfig — recurring for /mo prices',
        fn: () => { const c = generateStripeConfig(makeTestBrand()); const t1 = c.products.find(p => p.metadata.tier === 'tier1'); return t1?.recurring?.interval === 'month' || `Got ${JSON.stringify(t1?.recurring)}`; } },
      { id: 'FI-12', name: 'generateStripeConfig — taxSettings always present',
        fn: () => generateStripeConfig(makeTestBrand()).taxSettings.collectTax === true || 'Expected collectTax true' },
      { id: 'FI-13', name: 'generateChartOfAccounts — Revenue + COGS + OpEx parents',
        fn: () => { const c = generateChartOfAccounts(makeTestBrand()); const p = c.accounts.filter(a => a.parent === null).map(a => a.name); return (p.includes('Revenue') && p.includes('COGS') && p.includes('Operating Expenses')) || `Found: ${p.join(', ')}`; } },
      { id: 'FI-14', name: 'generateChartOfAccounts — Consultation Revenue when telehealth active',
        fn: () => generateChartOfAccounts(makeTestBrand()).accounts.some(a => a.name === 'Consultation Revenue') || 'Not found' },
      { id: 'FI-15', name: 'generateChartOfAccounts — no Consultation Revenue without telehealth',
        fn: () => !generateChartOfAccounts(makeTestBrand({ telehealthNetwork: '' })).accounts.some(a => a.name === 'Consultation Revenue') || 'Should not be present' },
      { id: 'FI-16', name: 'generateChartOfAccounts — Rx Upgrade Revenue when pharmacy set',
        fn: () => generateChartOfAccounts(makeTestBrand()).accounts.some(a => a.name === 'Rx Upgrade Revenue') || 'Not found' },
      { id: 'FI-17', name: 'generateChartOfAccounts — IP License Royalty when trust set',
        fn: () => generateChartOfAccounts(makeTestBrand()).accounts.some(a => a.name === 'IP License Royalty') || 'Not found' },
      { id: 'FI-18', name: 'generateChartOfAccounts — vendors populated from partners',
        fn: () => { const c = generateChartOfAccounts(makeTestBrand()); return c.vendors.length === 4 || `Got ${c.vendors.length}`; } },
      { id: 'FI-19', name: 'generateChartOfAccounts — classes from marketVertical',
        fn: () => { const c = generateChartOfAccounts(makeTestBrand()); return c.classes.length === 1 && c.classes[0].name === 'hormone_optimization' || `Got ${c.classes.length} classes`; } },
      { id: 'FI-20', name: 'generateSyncRules — includes charge.refunded rule',
        fn: () => !!generateSyncRules(makeTestBrand()).find(r => r.eventType === 'charge.refunded') || 'Not found' },
      { id: 'FI-21', name: 'generateSyncRules — subscription rules when paid tiers active',
        fn: () => { const r = generateSyncRules(makeTestBrand()); return (!!r.find(x => x.eventType === 'customer.subscription.created') && !!r.find(x => x.eventType === 'customer.subscription.deleted')) || 'Missing subscription rules'; } },
      { id: 'FI-22', name: 'generateSyncRules — invoice.paid rules match active streams',
        fn: () => { const r = generateSyncRules(makeTestBrand()).filter(x => x.eventType === 'invoice.paid'); return r.length === 4 || `Got ${r.length} invoice.paid rules`; } },
      { id: 'FI-23', name: 'generateSyncRules — all rules have unique IDs',
        fn: () => { const r = generateSyncRules(makeTestBrand()); const ids = r.map(x => x.id); return new Set(ids).size === ids.length || `${ids.length - new Set(ids).size} duplicates`; } },
      { id: 'FI-24', name: 'generatePharmacyReconRules — returns rule when pharmacy set',
        fn: () => { const r = generatePharmacyReconRules(makeTestBrand()); return r !== null && r.revenueSharePercent === 70 || 'Expected non-null with 70%'; } },
      { id: 'FI-25', name: 'generatePharmacyReconRules — null when no pharmacy',
        fn: () => generatePharmacyReconRules(makeTestBrand({ pharmacyPartner: '' })) === null || 'Expected null' },
      { id: 'FI-26', name: 'generatePharmacyReconRules — shares total 100%',
        fn: () => { const r = generatePharmacyReconRules(makeTestBrand()); if (!r) return 'Null'; return (r.revenueSharePercent + r.partnerSharePercent) === 100 || 'Not 100%'; } },
      { id: 'FI-27', name: 'generateInterEntityTransactions — royalty when trust configured',
        fn: () => !!generateInterEntityTransactions(makeTestBrand(), 1_000_000).find(t => t.type === 'royalty') || 'Not found' },
      { id: 'FI-28', name: 'generateInterEntityTransactions — royalty is 5% of revenue',
        fn: () => { const t = generateInterEntityTransactions(makeTestBrand(), 1_000_000).find(t => t.type === 'royalty'); return t?.amountCents === 50_000 || `Got ${t?.amountCents}`; } },
      { id: 'FI-29', name: 'generateInterEntityTransactions — PC retainer in cents',
        fn: () => { const t = generateInterEntityTransactions(makeTestBrand(), 500_000).find(t => t.type === 'management_fee'); return t?.amountCents === 250_000 || `Got ${t?.amountCents}`; } },
      { id: 'FI-30', name: 'generateInterEntityTransactions — MD retainer present',
        fn: () => { const t = generateInterEntityTransactions(makeTestBrand(), 500_000).find(t => t.type === 'retainer'); return !!t && t.amountCents === 300_000 || `Got ${t?.amountCents}`; } },
      { id: 'FI-31', name: 'generateInterEntityTransactions — no royalty without trust',
        fn: () => !generateInterEntityTransactions(makeTestBrand({ trustName: '', ipLicenseRoyaltyPercent: 0 }), 1_000_000).find(t => t.type === 'royalty') || 'Should not be present' },
      { id: 'FI-32', name: 'generateInterEntityTransactions — from/to use entity names',
        fn: () => { const t = generateInterEntityTransactions(makeTestBrand(), 1_000_000).find(t => t.type === 'royalty'); return t?.from === 'TestBrand LLC' && t?.to === 'TestBrand IP Trust' || `Got from=${t?.from} to=${t?.to}`; } },
    ],
  };
}

// ── 5. Codebase Intelligence (CI-1 → CI-15) ──

function buildCodebaseIntelligenceTests(): TestSuite {
  return {
    module: 'Codebase Intelligence',
    icon: 'CI',
    tests: [
      // scanContent
      { id: 'CI-1', name: 'scanContent — detects hardcoded brand IDs',
        fn: () => { const f = scanContent('const id = "hemologix-primary";', 'test.ts'); return f.length === 1 && f[0].type === 'HARDCODED_VALUE' || `Got ${f.length} findings`; } },
      { id: 'CI-2', name: 'scanContent — skips hardcoded values in comments',
        fn: () => scanContent('// brandId: "hemologix-primary"', 'test.ts').length === 0 || 'Should skip comments' },
      { id: 'CI-3', name: 'scanContent — detects TODO/FIXME/HACK markers',
        fn: () => { const f = scanContent('// TODO: fix\n// FIXME: broken\n// HACK: workaround', 'test.ts'); return f.length === 3 || `Got ${f.length}`; } },
      { id: 'CI-4', name: 'scanContent — detects "as any" type casts',
        fn: () => { const f = scanContent('const x = foo as any;', 'test.ts'); return f.length === 1 && f[0].type === 'TYPE_WORKAROUND' || 'Expected TYPE_WORKAROUND'; } },
      { id: 'CI-5', name: 'scanContent — skips TYPE_WORKAROUND in .d.ts files',
        fn: () => scanContent('export declare const x: any;', 'types.d.ts').filter(f => f.type === 'TYPE_WORKAROUND').length === 0 || 'Should skip .d.ts' },
      { id: 'CI-6', name: 'scanContent — detects empty catch blocks',
        fn: () => { const f = scanContent('try { risky() } catch (e) {}', 'test.ts'); return f.length === 1 && f[0].type === 'MISSING_ERROR_HANDLING' || 'Expected MISSING_ERROR_HANDLING'; } },
      { id: 'CI-7', name: 'scanContent — detects eval() usage as critical',
        fn: () => { const f = scanContent('const r = eval("1+1");', 'test.ts'); return f.length === 1 && f[0].severity === 'critical' || 'Expected critical SECURITY_GAP'; } },
      { id: 'CI-8', name: 'scanContent — detects unscoped Firestore collections',
        fn: () => { const f = scanContent('firestore.collection("orders").doc(id)', 'test.ts'); return f.length === 1 && f[0].type === 'MISSING_BRAND_SCOPING' || 'Expected MISSING_BRAND_SCOPING'; } },
      { id: 'CI-9', name: 'scanContent — deduplicates same line+type',
        fn: () => { const f = scanContent('brandId: "hemologix-primary"', 'test.ts'); return f.filter(x => x.type === 'HARDCODED_VALUE').length === 1 || 'Should deduplicate'; } },
      { id: 'CI-10', name: 'scanContent — clean code returns empty',
        fn: () => scanContent('export function add(a: number, b: number): number {\n  return a + b;\n}', 'test.ts').length === 0 || 'Expected empty' },
      // calculateDebtScore
      { id: 'CI-11', name: 'calculateDebtScore — 100 for zero findings',
        fn: () => calculateDebtScore([], 100) === 100 || `Got ${calculateDebtScore([], 100)}` },
      { id: 'CI-12', name: 'calculateDebtScore — normalizes by file count',
        fn: () => {
          const f = { type: 'STUBBED_FUNCTION' as const, severity: 'medium' as const, file: 'test.ts', line: 1, description: '', snippet: '' };
          return calculateDebtScore([f], 1000) > calculateDebtScore([f], 10) || 'Large codebase should score higher';
        },
      },
      // calculateHotspots
      { id: 'CI-13', name: 'calculateHotspots — ranks files descending',
        fn: () => {
          const findings = [
            { type: 'STUBBED_FUNCTION' as const, severity: 'medium' as const, file: 'a.ts', line: 1, description: '', snippet: '' },
            { type: 'STUBBED_FUNCTION' as const, severity: 'medium' as const, file: 'b.ts', line: 1, description: '', snippet: '' },
            { type: 'STUBBED_FUNCTION' as const, severity: 'medium' as const, file: 'b.ts', line: 2, description: '', snippet: '' },
          ];
          const h = calculateHotspots(findings);
          return h[0].file === 'b.ts' && h[0].count === 2 || `Got ${h[0]?.file}:${h[0]?.count}`;
        },
      },
      // suggestFix
      { id: 'CI-14', name: 'suggestFix — returns value for all 10 debt types',
        fn: () => {
          const types = ['HARDCODED_VALUE','STUBBED_FUNCTION','TYPE_WORKAROUND','MISSING_ERROR_HANDLING','PLACEHOLDER_UI','DEAD_CODE','HARDCODED_PROMPT','STALE_REFERENCE','MISSING_BRAND_SCOPING','SECURITY_GAP'] as const;
          for (const t of types) { if (!suggestFix(t, 'test')) return `No fix for ${t}`; }
          return true;
        },
      },
      // SEVERITY_WEIGHT
      { id: 'CI-15', name: 'SEVERITY_WEIGHT — critical > high > medium > low',
        fn: () => SEVERITY_WEIGHT.critical > SEVERITY_WEIGHT.high && SEVERITY_WEIGHT.high > SEVERITY_WEIGHT.medium && SEVERITY_WEIGHT.medium > SEVERITY_WEIGHT.low || 'Wrong order' },
    ],
  };
}

// ════════════════════════════════════════════
// Test Runner Component
// ════════════════════════════════════════════

export function UnitTestsTab() {
  const [results, setResults] = useState<TestResult[]>([]);
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState<string | null>(null);

  const runAllTests = useCallback(async () => {
    setRunning(true);
    const suites = buildAllSuites();
    const allResults: TestResult[] = [];

    for (const suite of suites) {
      for (const test of suite.tests) {
        allResults.push({ id: test.id, name: test.name, module: suite.module, status: 'pending' });
      }
    }
    setResults([...allResults]);

    for (const suite of suites) {
      for (const test of suite.tests) {
        const idx = allResults.findIndex(r => r.id === test.id);
        allResults[idx] = { ...allResults[idx], status: 'running' };
        setResults([...allResults]);

        const start = performance.now();
        try {
          const result = test.fn();
          const duration = Math.round(performance.now() - start);
          if (result === true) {
            allResults[idx] = { ...allResults[idx], status: 'pass', durationMs: duration };
          } else {
            allResults[idx] = { ...allResults[idx], status: 'fail', message: typeof result === 'string' ? result : 'Test returned false', durationMs: duration };
          }
        } catch (err: any) {
          const duration = Math.round(performance.now() - start);
          allResults[idx] = { ...allResults[idx], status: 'fail', message: err.message || String(err), durationMs: duration };
        }
        setResults([...allResults]);
        await new Promise(r => setTimeout(r, 20));
      }
    }

    setLastRun(new Date().toLocaleTimeString());
    setRunning(false);
  }, []);

  const passed = results.filter(r => r.status === 'pass').length;
  const failed = results.filter(r => r.status === 'fail').length;
  const total = results.length;
  const modules = [...new Set(results.map(r => r.module))];

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <p style={{ fontSize: 13, color: C.textMuted, margin: 0 }}>
            {total > 0
              ? `${passed}/${total} passed${failed > 0 ? ` · ${failed} failed` : ''}${lastRun ? ` · Last run ${lastRun}` : ''}`
              : '5 modules · 76 tests · Click Run All to verify'}
          </p>
        </div>
        <button
          onClick={runAllTests}
          disabled={running}
          style={{
            padding: '8px 20px',
            fontSize: 13,
            fontWeight: 600,
            fontFamily: "'DM Sans', sans-serif",
            borderRadius: 4,
            border: 'none',
            cursor: running ? 'not-allowed' : 'pointer',
            background: running ? C.border : C.rose,
            color: running ? C.textMuted : '#fff',
          }}
        >
          {running ? 'Running...' : results.length > 0 ? 'Re-run All' : 'Run All Tests'}
        </button>
      </div>

      {/* Score cards */}
      {results.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
          <MiniCard label="Total" value={total} color={C.text} />
          <MiniCard label="Passed" value={passed} color={C.pass} />
          <MiniCard label="Failed" value={failed} color={failed > 0 ? C.fail : C.pass} />
          <MiniCard label="Last Run" value={lastRun || '--'} color={C.rose} isText />
        </div>
      )}

      {/* Module results */}
      {modules.map(mod => {
        const modResults = results.filter(r => r.module === mod);
        const modPassed = modResults.filter(r => r.status === 'pass').length;
        const modFailed = modResults.filter(r => r.status === 'fail').length;
        const allPassed = modPassed === modResults.length;

        return (
          <div key={mod} style={{
            background: C.bgCard,
            border: `1px solid ${C.border}`,
            borderRadius: 4,
            marginBottom: 12,
            overflow: 'hidden',
          }}>
            {/* Module header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 16px',
              borderBottom: `1px solid ${C.border}`,
              background: '#FAFAFA',
            }}>
              <span style={{
                background: modFailed > 0 ? C.failBg : allPassed ? C.passBg : '#F4F4F5',
                color: modFailed > 0 ? C.fail : allPassed ? C.pass : C.textMuted,
                fontSize: 11,
                fontWeight: 700,
                padding: '3px 8px',
                borderRadius: 4,
                fontFamily: 'monospace',
              }}>
                {modResults[0]?.id.split('-')[0] || mod.slice(0, 2).toUpperCase()}
              </span>
              <span style={{ fontSize: 14, fontWeight: 600, color: C.text, fontFamily: "'DM Sans', sans-serif" }}>
                {mod}
              </span>
              <span style={{ marginLeft: 'auto', fontSize: 12, color: C.textMuted }}>
                {modPassed}/{modResults.length}
              </span>
            </div>

            {/* Test rows */}
            {modResults.map(r => (
              <div key={r.id} style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 16px',
                borderBottom: `1px solid ${C.border}`,
                fontSize: 13,
              }}>
                <span style={{
                  background: r.status === 'pass' ? C.passBg : r.status === 'fail' ? C.failBg : r.status === 'running' ? C.warnBg : '#F4F4F5',
                  color: r.status === 'pass' ? C.pass : r.status === 'fail' ? C.fail : r.status === 'running' ? C.warn : C.textMuted,
                  fontSize: 10,
                  fontWeight: 700,
                  padding: '2px 6px',
                  borderRadius: 3,
                  fontFamily: 'monospace',
                  minWidth: 36,
                  textAlign: 'center' as const,
                  display: 'inline-block',
                }}>
                  {r.status === 'pass' ? 'PASS' : r.status === 'fail' ? 'FAIL' : r.status === 'running' ? '...' : '--'}
                </span>
                <span style={{ color: C.textMuted, fontFamily: 'monospace', fontSize: 11, minWidth: 40 }}>
                  {r.id}
                </span>
                <span style={{ color: r.status === 'fail' ? C.fail : C.text, flex: 1, fontFamily: "'DM Sans', sans-serif" }}>
                  {r.name}
                </span>
                {r.durationMs !== undefined && (
                  <span style={{ color: C.textMuted, fontSize: 11 }}>{r.durationMs}ms</span>
                )}
                {r.message && (
                  <span style={{
                    color: C.fail,
                    fontSize: 11,
                    maxWidth: 220,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap' as const,
                  }} title={r.message}>
                    {r.message}
                  </span>
                )}
              </div>
            ))}
          </div>
        );
      })}

      {/* Empty state */}
      {results.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: 48,
          color: C.textMuted,
          background: C.bgCard,
          borderRadius: 4,
          border: `1px solid ${C.border}`,
        }}>
          <p style={{ fontSize: 15, color: C.text, fontWeight: 600, marginBottom: 8 }}>
            No tests run yet
          </p>
          <p style={{ fontSize: 13, color: C.textSec }}>
            Click "Run All Tests" to verify module integrity across Event Bus, Business Architect, Master Formulator, Financial, and Codebase Intelligence.
          </p>
        </div>
      )}
    </div>
  );
}

function MiniCard({ label, value, color, isText }: {
  label: string; value: number | string; color: string; isText?: boolean;
}) {
  return (
    <div style={{
      background: C.bgCard,
      border: `1px solid ${C.border}`,
      borderRadius: 4,
      padding: '10px 14px',
      textAlign: 'center' as const,
    }}>
      <div style={{ fontSize: isText ? 13 : 24, fontWeight: 700, color, fontFamily: "'DM Sans', sans-serif" }}>
        {value}
      </div>
      <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>{label}</div>
    </div>
  );
}
